import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Typography,
  notification,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Space,
  Divider,
  Alert,
  Tag
} from "antd";
import axios from "axios";
import { 
  UserOutlined, 
  PhoneOutlined, 
  CalendarOutlined, 
  CheckCircleOutlined,
  CustomerServiceOutlined,
  EditOutlined,
  SaveOutlined,
  MessageOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { useHistory, useParams } from "react-router-dom";
import { baseUrl } from "../../config";

const { Title, Text } = Typography;
const { Item } = Form;
const { Option } = Select;
const { TextArea } = Input;

function InquiryForm({ userId, onRefreshData = () => {} }) {
  const history = useHistory();
  const [form] = Form.useForm();
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inquiryDate, setInquiryDate] = useState(null);
  const [resolvedDate, setResolvedDate] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [status, setStatus] = useState('Pending');
  const [staffList, setStaffList] = useState([]);
  const [previousInquiries, setPreviousInquiries] = useState([]);
  const [showPreviousInquiries, setShowPreviousInquiries] = useState(false);

  // Phone numbers for received calls
  const phoneNumbers = [
    "9001000071", "7023131008", "9030301008", "9030191008", "9030131008",
    "9492861008", "9492871008", "9492881008", "9951513144", "9951516145", "9951516155"
  ];

  useEffect(() => {
    getStaffList();
    if (userId) {
      setIsUpdateMode(true);
      fetchInquiryDetails();
    }
  }, [userId]);

  const fetchInquiryDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/inquiry/read/${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (response.status === 200) {
        const data = response.data.result;
        form.setFieldsValue(data);
        
        // Handle Inquiry Date
        if (data.inquiry_date && data.inquiry_date !== '0000-00-00' && moment(data.inquiry_date).isValid()) {
          setInquiryDate(data.inquiry_date);
        } else {
          setInquiryDate(null);
        }
        
        // Handle Resolved Date
        if (data.resolved_date && data.resolved_date !== '0000-00-00' && moment(data.resolved_date).isValid()) {
          setResolvedDate(data.resolved_date);
        } else {
          setResolvedDate(null);
        }
        
        // Handle Start Date
        if (data.start_date && data.start_date !== '0000-00-00' && moment(data.start_date).isValid()) {
          setStartDate(data.start_date);
        } else {
          setStartDate(null);
        }
        
        // Handle Status
        setStatus(data.status || 'Pending');

        // Check for previous inquiries with this phone number
        if (data.phone_no) {
          checkPreviousInquiries(data.phone_no);
        }
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to fetch inquiry details',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
    }
  };

  const getStaffList = async () => {
    try {
      const response = await axios.get(`${baseUrl}/staff/list`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        }
      });
      if (response.data.status) {
        setStaffList(response.data.result);
      }
    } catch (error) {
      console.error("Staff list API error:", error);
    }
  };

  const checkPreviousInquiries = async (phoneNumber) => {
    if (phoneNumber && phoneNumber.length >= 10) {
      try {
        const response = await axios.get(`${baseUrl}/inquiry/list?phone_no=${phoneNumber}&limit=5`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          }
        });
        if (response.data.status && response.data.result.length > 0) {
          // Filter out current inquiry if in update mode
          const filteredInquiries = isUpdateMode 
            ? response.data.result.filter(inquiry => inquiry.id != userId)
            : response.data.result;
          
          if (filteredInquiries.length > 0) {
            setPreviousInquiries(filteredInquiries);
            setShowPreviousInquiries(true);
          } else {
            setPreviousInquiries([]);
            setShowPreviousInquiries(false);
          }
        } else {
          setPreviousInquiries([]);
          setShowPreviousInquiries(false);
        }
      } catch (error) {
        console.error("Error checking previous inquiries:", error);
        setPreviousInquiries([]);
        setShowPreviousInquiries(false);
      }
    } else {
      setPreviousInquiries([]);
      setShowPreviousInquiries(false);
    }
  };

  const handlePhoneNumberChange = (e) => {
    const phoneNumber = e.target.value;
    
    // Debounce the API call
    clearTimeout(window.phoneCheckTimeout);
    window.phoneCheckTimeout = setTimeout(() => {
      checkPreviousInquiries(phoneNumber);
    }, 500);
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);

      // Handle Inquiry Date
      if (inquiryDate && inquiryDate !== '0000-00-00') {
        const formattedDate = moment(inquiryDate).format('YYYY-MM-DD');
        values.inquiry_date = formattedDate;
      }

      // Handle Resolved Date
      if (resolvedDate && resolvedDate !== '0000-00-00') {
        const formattedDate = moment(resolvedDate).format('YYYY-MM-DD');
        values.resolved_date = formattedDate;
      }

      // Handle Start Date
      if (startDate && startDate !== '0000-00-00') {
        const formattedDate = moment(startDate).format('YYYY-MM-DD');
        values.start_date = formattedDate;
      }

      // Make API request
      let response;
      if (isUpdateMode) {
        response = await axios.patch(`${baseUrl}/inquiry/update/${userId}`, values, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });
      } else {
        response = await axios.post(`${baseUrl}/inquiry/create`, values, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });
      }

      if (response.status === 200) {
        notification.success({
          message: 'Success',
          description: isUpdateMode ? 'Inquiry updated successfully!' : 'Inquiry created successfully!',
          placement: 'topRight'
        });
        
        onRefreshData();
        
        if (!isUpdateMode) {
          form.resetFields();
          setInquiryDate(null);
          setResolvedDate(null);
          setStartDate(null);
          setStatus('Pending');
          setPreviousInquiries([]);
          setShowPreviousInquiries(false);
        }
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'Operation failed',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInquiryDateChange = (date) => {
    if (date) {
      const formattedDate = moment(date).format('YYYY-MM-DD');
      setInquiryDate(formattedDate);
    } else {
      setInquiryDate(null);
    }
  };

  const handleResolvedDateChange = (date) => {
    if (date) {
      const formattedDate = moment(date).format('YYYY-MM-DD');
      setResolvedDate(formattedDate);
    } else {
      setResolvedDate(null);
    }
  };

  const handleStartDateChange = (date) => {
    if (date) {
      const formattedDate = moment(date).format('YYYY-MM-DD');
      setStartDate(formattedDate);
    } else {
      setStartDate(null);
    }
  };

  const handleStatusChange = (value) => {
    setStatus(value);
    // If status is not InProcess, clear start date
    if (value !== 'InProcess') {
      setStartDate(null);
    }
  };

  const getStatusInfo = (statusValue) => {
    switch (statusValue) {
      case 'Pending':
        return {
          color: '#fa8c16',
          icon: <ClockCircleOutlined />,
          description: 'Inquiry is waiting to be processed'
        };
      case 'InProcess':
        return {
          color: '#7a0000',
          icon: <SyncOutlined spin />,
          description: 'Inquiry is currently being worked on'
        };
      case 'Completed':
        return {
          color: '#52c41a',
          icon: <CheckCircleOutlined />,
          description: 'Inquiry has been resolved successfully'
        };
      default:
        return {
          color: '#d9d9d9',
          icon: <ClockCircleOutlined />,
          description: 'Select inquiry status'
        };
    }
  };

  const statusInfo = getStatusInfo(status);

  return (
    <Form
      form={form}
      onFinish={onFinish}
      layout="vertical"
      initialValues={{ status: 'Pending' }}
    >
      {/* Customer Information */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
        Customer Information
      </div>
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={12} lg={8}>
          <Item
            label="Phone Number"
            name="phone_no"
            rules={[
              { required: true, message: 'Please enter phone number' },
              { pattern: /^\d{10,12}$/, message: 'Phone must be 10-12 digits!' }
            ]}
          >
            <Input
              placeholder="Enter customer phone number"
              type="number"
              onChange={handlePhoneNumberChange}
            />
          </Item>
          {showPreviousInquiries && (
            <Alert
              message={<Text strong>Previous Inquiries Found</Text>}
              description={
                <div>
                  <Text>This number has {previousInquiries.length} previous inquiry(ies):</Text>
                  <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 16 }}>
                    {previousInquiries.slice(0, 3).map((inquiry) => (
                      <li key={inquiry.id} style={{ marginBottom: 3 }}>
                        <Text style={{ fontSize: 12 }}>
                          <strong>{inquiry.inquiry_regards || 'General Inquiry'}</strong>
                          <Tag color={inquiry.status === 'Completed' ? 'green' : inquiry.status === 'InProcess' ? 'blue' : 'orange'}
                            style={{ marginLeft: 6, fontSize: 10 }}>
                            {inquiry.status}
                          </Tag>
                        </Text>
                      </li>
                    ))}
                  </ul>
                  {previousInquiries.length > 3 && (
                    <Text type="secondary" style={{ fontSize: 12 }}>...and {previousInquiries.length - 3} more</Text>
                  )}
                </div>
              }
              type="info"
              showIcon={false}
              style={{ marginTop: 8, marginBottom: 12, borderRadius: 6 }}
            />
          )}
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Please enter name' }]}
          >
            <Input placeholder="Enter name" />
          </Item>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Item label="Received Call Number" name="received_call">
            <Select
              placeholder="Select received call number"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {phoneNumbers.map((phone) => (
                <Option key={phone} value={phone}>{phone}</Option>
              ))}
            </Select>
          </Item>
        </Col>
      </Row>

      {/* Inquiry Details */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #f0f0f0', marginTop: 8 }}>
        Inquiry Details
      </div>
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Item label="Inquiry Regards" name="inquiry_regards">
            <TextArea placeholder="Describe what the inquiry is about..." rows={3} maxLength={500} showCount />
          </Item>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Item label="Inquiry Date">
            <DatePicker
              onChange={handleInquiryDateChange}
              value={inquiryDate && inquiryDate !== "0000-00-00" && moment(inquiryDate).isValid() ? moment(inquiryDate) : null}
              format="DD-MM-YYYY"
              placeholder="Select inquiry date"
              style={{ width: '100%' }}
              allowClear
            />
          </Item>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Item label="Attended By" name="attend_by">
            <Select
              placeholder="Select staff who attended"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {staffList.map((staff) => (
                <Option key={staff.id} value={staff.id}>{staff.firstName} {staff.lastName}</Option>
              ))}
            </Select>
          </Item>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Item label="Assigned To" name="assigned_to">
            <Select
              placeholder="Select staff to assign"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {staffList.map((staff) => (
                <Option key={staff.id} value={staff.id}>{staff.firstName} {staff.lastName}</Option>
              ))}
            </Select>
          </Item>
        </Col>
      </Row>

      {/* Status & Resolution */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #f0f0f0', marginTop: 8 }}>
        Status & Resolution
      </div>
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={12} lg={8}>
          <Item label="Inquiry Status" name="status" rules={[{ required: true, message: 'Please select status' }]}>
            <Select value={status} onChange={handleStatusChange} placeholder="Select inquiry status">
              <Option value="Pending">Pending</Option>
              <Option value="InProcess">In Process</Option>
              <Option value="Completed">Completed</Option>
            </Select>
          </Item>
        </Col>

        {status === 'InProcess' && (
          <Col xs={24} sm={12} lg={8}>
            <Item label="Work Start Date">
              <DatePicker
                onChange={handleStartDateChange}
                value={startDate && startDate !== "0000-00-00" && moment(startDate).isValid() ? moment(startDate) : null}
                format="DD-MM-YYYY"
                placeholder="Select work start date"
                style={{ width: '100%' }}
                allowClear
              />
            </Item>
          </Col>
        )}

        <Col xs={24} sm={12} lg={8}>
          <Item label="Resolved Date">
            <DatePicker
              onChange={handleResolvedDateChange}
              value={resolvedDate && resolvedDate !== "0000-00-00" && moment(resolvedDate).isValid() ? moment(resolvedDate) : null}
              format="DD-MM-YYYY"
              placeholder="Select resolved date"
              style={{ width: '100%' }}
              allowClear
            />
          </Item>
        </Col>

        <Col xs={24}>
          <Item label="Resolution Remark" name="resolved_remark">
            <TextArea placeholder="Describe how the inquiry was resolved..." rows={3} maxLength={500} showCount />
          </Item>
        </Col>
      </Row>

      <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
        <Button onClick={onRefreshData} style={{ borderRadius: 8 }}>Cancel</Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          icon={isUpdateMode ? <EditOutlined /> : <SaveOutlined />}
          style={{ background: '#7a0000', borderColor: '#7a0000', borderRadius: 8 }}
        >
          {isUpdateMode ? 'Update Inquiry' : 'Save Inquiry'}
        </Button>
      </div>
    </Form>
  );
}

export default InquiryForm;